import React, { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Alert,
  Paper,
  Avatar,
  alpha,
  useTheme
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { TwoFactorSettings } from '../components/Auth/TwoFactorSettings';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

export const SecuritySettings: React.FC = () => {
  const theme = useTheme();
  const { user, refreshUser } = useAuth();
  const [success, setSuccess] = useState<string | null>(null);

  const handleUpdate = async () => {
    try {
      await refreshUser();
      setSuccess('Podešavanja su uspešno ažurirana!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Failed to update user:', error);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Molimo prijavite se da biste pristupili bezbednosnim podešavanjima.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Paper sx={{
        p: 4,
        mb: 4,
        borderRadius: 3,
        background: `linear-gradient(135deg,
          ${theme.palette.primary.main} 0%,
          ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '& *': {
          color: 'white !important'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="center" gap={3}>
              <Avatar sx={{
                width: 60,
                height: 60,
                background: alpha('#fff', 0.2)
              }}>
                <SecurityIcon sx={{ fontSize: 30 }} />
              </Avatar>
              <Box>
                <h2 style={{
                  color: '#ffffff',
                  margin: '0 0 8px 0',
                  fontWeight: 700,
                  fontSize: '2rem',
                  fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
                }}>
                  Bezbednosna Podešavanja
                </h2>
                <h3 style={{
                  color: '#ffffff',
                  margin: 0,
                  fontWeight: 400,
                  fontSize: '1.1rem',
                  opacity: 0.9,
                  fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
                }}>
                  Upravljajte bezbednošću naloga i podešavanjima autentifikacije
                </h3>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Account Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informacije o Nalogu
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email Adresa
                </Typography>
                <Typography variant="body1">
                  {user.email}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Kompanija
                </Typography>
                <Typography variant="body1">
                  {user.company_name || 'Nije navedeno'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Uloga
                </Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                  {user.role}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status Naloga
                </Typography>
                <Typography
                  variant="body1"
                  color={user.is_active ? 'success.main' : 'error.main'}
                >
                  {user.is_active ? 'Aktivan' : 'Neaktivan'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Two-Factor Authentication */}
        <Grid size={{ xs: 12, md: 6 }}>
          <TwoFactorSettings
            user={user}
            onUpdate={handleUpdate}
          />
        </Grid>

        {/* Password Management */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upravljanje Lozinkama
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Funkcije upravljanja lozinkama će biti dostupne u budućem ažuriranju.
                Za sada, molimo kontaktirajte administratora da promenite lozinku.
              </Typography>

              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Zahtevi za Lozinku:</strong><br />
                  • Najmanje 8 karaktera<br />
                  • Mora sadržavati velika i mala slova<br />
                  • Mora sadržavati najmanje jedan broj<br />
                  • Mora sadržavati najmanje jedan specijalni karakter
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
